import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { seguridadApi } from './seguridadApi';

/**
 * S-5 Politicas de acceso (ADR-002 S4/S5): segundo factor y geocerca por rol, excepciones por
 * usuario e interruptores generales. Todo rige al momento, sin reiniciar el sistema.
 */
const SEGUNDO_FACTOR = [
    { label: 'Obligatorio', value: 'OBLIGATORIO' },
    { label: 'Opcional', value: 'OPCIONAL' },
    { label: 'No aplica', value: 'NO_APLICA' }
];
const SI_NO = [{ label: 'Si', value: 'SI' }, { label: 'No', value: 'NO' }];
const DEL_ROL = { label: 'Como su rol', value: null };

const INTERRUPTORES = {
    SEG_PERMISOS_SERVIDOR: {
        titulo: 'Permisos validados en el servidor',
        ayuda: 'AUDITAR registra lo que se bloquearia sin bloquear; BLOQUEAR responde "sin permiso". Pasar a BLOQUEAR cuando la bitacora no muestre accesos legitimos fuera de permiso.',
        opciones: [{ label: 'Auditar', value: 'AUDITAR' }, { label: 'Bloquear', value: 'BLOQUEAR' }]
    },
    SEG_SEGUNDO_FACTOR: { titulo: 'Exigir segundo factor', ayuda: 'Aplica la columna "Segundo factor" de cada rol y las excepciones por usuario.' },
    SEG_GEOCERCA: { titulo: 'Exigir geocerca', ayuda: 'Aplica la columna "Geocerca" de cada rol: ingreso y uso solo dentro de la zona y/o desde las IP indicadas.' },
    SEG_MIGRAR_CONTRASENAS: {
        titulo: 'Cifrar contrasenas al ingresar',
        ayuda: 'Cada usuario que ingresa queda con su contrasena cifrada (BCrypt). No tiene vuelta atras para ese usuario: prenderlo cuando la version nueva este validada.'
    },
    SEG_BLOQUEO_INTENTOS: { titulo: 'Bloqueo por intentos fallidos', ayuda: 'Formato intentos;minutos. Ejemplo 5;15 = cinco fallos seguidos bloquean 15 minutos.', texto: true },
    SEG_UBICACION_INGRESO: { titulo: 'Pedir ubicacion al ingresar', ayuda: 'Despues de ingresar, el navegador pregunta una vez si comparte la ubicacion, para el mapa de sesiones. Si no la comparte, la sesion se ubica en la sede de su IP.' },
    SEG_TOKEN: { titulo: 'Duracion de accesos', ayuda: 'minutos del acceso web;horas que dura la sesion web sin uso;dias del token de la app movil. Ejemplo 15;12;90.', texto: true, siempre: true }
};

const SeguridadPoliticas = () => {
    const toast = useRef(null);
    const [datos, setDatos] = useState(null);
    const [usuarios, setUsuarios] = useState([]);
    const [editRol, setEditRol] = useState(null);
    const [editUsuario, setEditUsuario] = useState(null);
    const [parametros, setParametros] = useState({});

    const avisar = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === 'error' ? 'Error' : 'Guardado', detail, life: 4000 });

    const cargar = useCallback(async () => {
        try {
            const [p, u] = await Promise.all([seguridadApi.politicas(), seguridadApi.usuarios()]);
            setDatos(p);
            setUsuarios(u);
            setParametros(Object.fromEntries((p.interruptores || []).map((i) => [i.codigo, i.parametro || ''])));
        } catch (e) {
            avisar('error', e.message);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const geocercas = useMemo(() => [{ label: 'Sin zona', value: null }]
        .concat(((datos && datos.geocercas) || []).map((g) => ({ label: g.nombre, value: g.ubicacion_id }))), [datos]);
    const nombreGeocerca = (id) => (geocercas.find((g) => g.value === id) || {}).label || '';

    const guardarRol = async () => {
        try {
            await seguridadApi.guardarRol(editRol.rol_id, {
                segundoFactor: editRol.segundo_factor, geocerca: editRol.geocerca,
                geocercaUbicacionId: editRol.geocerca_ubicacion_id, ipsPermitidas: editRol.ips_permitidas
            });
            avisar('success', `Politica de ${editRol.rol}`);
            setEditRol(null);
            cargar();
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const guardarUsuario = async () => {
        try {
            await seguridadApi.guardarUsuario(editUsuario.usuario_id, {
                segundoFactor: editUsuario.politica_segundo_factor, geocerca: editUsuario.politica_geocerca,
                geocercaUbicacionId: editUsuario.geocerca_ubicacion_id, ipsPermitidas: editUsuario.ips_permitidas
            });
            avisar('success', `Excepcion de ${editUsuario.username}`);
            setEditUsuario(null);
            cargar();
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const cambiarInterruptor = (i, activo) => {
        const def = INTERRUPTORES[i.codigo] || {};
        confirmDialog({
            header: def.titulo || i.codigo,
            message: `¿${activo ? 'Prender' : 'Apagar'} "${def.titulo || i.codigo}"? Rige en menos de un minuto para todos los usuarios.`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Si',
            rejectLabel: 'No',
            accept: async () => {
                try {
                    await seguridadApi.interruptor(i.codigo, { activo, parametro: parametros[i.codigo] });
                    avisar('success', def.titulo || i.codigo);
                    cargar();
                } catch (e) {
                    avisar('error', e.message);
                }
            }
        });
    };

    const etiquetaSf = (v) => ({ OBLIGATORIO: <Tag severity="danger" value="Obligatorio" />, OPCIONAL: <Tag severity="warning" value="Opcional" /> }[v]
        || <span className="text-500">No aplica</span>);

    if (!datos) return <div className="card"><Toast ref={toast} />Cargando politicas…</div>;

    return (
        <div className="grid">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="col-12"><h4 className="m-0">Politicas de acceso</h4></div>

            <div className="col-12">
                <div className="card">
                    <h5>Interruptores generales</h5>
                    <div className="grid">
                        {(datos.interruptores || []).map((i) => {
                            const def = INTERRUPTORES[i.codigo] || { titulo: i.codigo };
                            return (
                                <div key={i.codigo} className="col-12 md:col-6 xl:col-4">
                                    <div className="border-1 surface-border border-round p-3 h-full flex flex-column gap-2">
                                        <div className="flex align-items-center justify-content-between gap-2">
                                            <label htmlFor={`int-${i.codigo}`} className="font-bold">{def.titulo}</label>
                                            {!def.siempre && <InputSwitch inputId={`int-${i.codigo}`} checked={!!i.activo} onChange={(e) => cambiarInterruptor(i, e.value)} />}
                                        </div>
                                        <small className="text-600">{def.ayuda}</small>
                                        {def.opciones && (
                                            <Dropdown value={parametros[i.codigo]} options={def.opciones}
                                                onChange={(e) => setParametros({ ...parametros, [i.codigo]: e.value })} />
                                        )}
                                        {def.texto && (
                                            <InputText value={parametros[i.codigo] || ''} onChange={(e) => setParametros({ ...parametros, [i.codigo]: e.target.value })} />
                                        )}
                                        {(def.opciones || def.texto) && parametros[i.codigo] !== (i.parametro || '') && (
                                            <Button label="Guardar valor" icon="pi pi-save" className="p-button-sm align-self-start" onClick={() => cambiarInterruptor(i, def.siempre ? true : !!i.activo)} />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {(datos.interruptores || []).length === 0 && (
                            <div className="col-12 text-600">Los interruptores aun no existen en la base de datos (script 02 de Casos/20261015_1600_seguridad_opem).</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="col-12">
                <div className="card">
                    <h5>Por rol</h5>
                    <p className="text-600 mt-0">Si un usuario tiene varios roles, rige el mas estricto. Clic en una fila para editar.</p>
                    <DataTable value={datos.roles} size="small" paginator rows={20} rowHover onRowClick={(e) => setEditRol({ ...e.data })}
                        style={{ cursor: 'pointer' }} responsiveLayout="scroll">
                        <Column header="Rol" field="rol" sortable />
                        <Column header="Usuarios" field="usuarios" sortable style={{ textAlign: 'right' }} />
                        <Column header="Segundo factor" body={(r) => etiquetaSf(r.segundo_factor)} />
                        <Column header="Geocerca" body={(r) => (r.geocerca === 'SI'
                            ? <span><Tag severity="info" value="Si" /> {nombreGeocerca(r.geocerca_ubicacion_id)} {r.ips_permitidas}</span>
                            : <span className="text-500">No</span>)} />
                    </DataTable>
                </div>
            </div>

            <div className="col-12">
                <div className="card">
                    <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                        <h5 className="m-0">Excepciones por usuario</h5>
                        <Dropdown placeholder="Agregar excepcion para…" filter options={usuarios.map((u) => ({ label: `${u.username} · ${u.nombre || ''}`, value: u }))}
                            onChange={(e) => setEditUsuario({ ...e.value })} style={{ minWidth: '16rem' }} />
                    </div>
                    <DataTable value={datos.excepciones} size="small" emptyMessage="Sin excepciones: todos siguen la politica de su rol" rowHover
                        onRowClick={(e) => setEditUsuario({ ...e.data })} style={{ cursor: 'pointer' }} responsiveLayout="scroll">
                        <Column header="Usuario" field="username" />
                        <Column header="Nombre" field="nombre" />
                        <Column header="Segundo factor" body={(u) => (u.politica_segundo_factor ? etiquetaSf(u.politica_segundo_factor) : <span className="text-500">Como su rol</span>)} />
                        <Column header="Geocerca" body={(u) => (u.politica_geocerca ? `${u.politica_geocerca === 'SI' ? 'Si' : 'No'} ${nombreGeocerca(u.geocerca_ubicacion_id)} ${u.ips_permitidas || ''}` : 'Como su rol')} />
                    </DataTable>
                </div>
            </div>

            <Dialog header={editRol ? `Politica del rol ${editRol.rol}` : ''} visible={!!editRol} style={{ width: 'min(32rem, 95vw)' }} onHide={() => setEditRol(null)}
                footer={<Button label="Guardar" icon="pi pi-save" onClick={guardarRol} />}>
                {editRol && (
                    <div className="flex flex-column gap-3">
                        <label htmlFor="rol-sf">Segundo factor</label>
                        <Dropdown inputId="rol-sf" value={editRol.segundo_factor} options={SEGUNDO_FACTOR} onChange={(e) => setEditRol({ ...editRol, segundo_factor: e.value })} />
                        <label htmlFor="rol-geo">Geocerca</label>
                        <Dropdown inputId="rol-geo" value={editRol.geocerca} options={SI_NO} onChange={(e) => setEditRol({ ...editRol, geocerca: e.value })} />
                        {editRol.geocerca === 'SI' && (
                            <>
                                <label htmlFor="rol-zona">Zona (poligono de Ubicaciones)</label>
                                <Dropdown inputId="rol-zona" value={editRol.geocerca_ubicacion_id} options={geocercas} filter onChange={(e) => setEditRol({ ...editRol, geocerca_ubicacion_id: e.value })} />
                                <label htmlFor="rol-ips">IP de la red del local (opcional, separadas por ;)</label>
                                <InputText id="rol-ips" value={editRol.ips_permitidas || ''} onChange={(e) => setEditRol({ ...editRol, ips_permitidas: e.target.value })} />
                                <small className="text-600">La ubicacion la informa el navegador; exigir tambien la IP del local es la regla fuerte.</small>
                            </>
                        )}
                    </div>
                )}
            </Dialog>

            <Dialog header={editUsuario ? `Excepcion de ${editUsuario.username}` : ''} visible={!!editUsuario} style={{ width: 'min(32rem, 95vw)' }} onHide={() => setEditUsuario(null)}
                footer={<Button label="Guardar" icon="pi pi-save" onClick={guardarUsuario} />}>
                {editUsuario && (
                    <div className="flex flex-column gap-3">
                        <label htmlFor="usu-sf">Segundo factor</label>
                        <Dropdown inputId="usu-sf" value={editUsuario.politica_segundo_factor ?? null} options={[DEL_ROL, ...SEGUNDO_FACTOR]}
                            onChange={(e) => setEditUsuario({ ...editUsuario, politica_segundo_factor: e.value })} />
                        <label htmlFor="usu-geo">Geocerca</label>
                        <Dropdown inputId="usu-geo" value={editUsuario.politica_geocerca ?? null} options={[DEL_ROL, ...SI_NO]}
                            onChange={(e) => setEditUsuario({ ...editUsuario, politica_geocerca: e.value })} />
                        <label htmlFor="usu-zona">Zona propia (opcional)</label>
                        <Dropdown inputId="usu-zona" value={editUsuario.geocerca_ubicacion_id ?? null} options={geocercas} filter
                            onChange={(e) => setEditUsuario({ ...editUsuario, geocerca_ubicacion_id: e.value })} />
                        <label htmlFor="usu-ips">IP propias (opcional, separadas por ;)</label>
                        <InputText id="usu-ips" value={editUsuario.ips_permitidas || ''} onChange={(e) => setEditUsuario({ ...editUsuario, ips_permitidas: e.target.value })} />
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default SeguridadPoliticas;
